import { useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { appendAntiForgeryToken, extractHtmlError, normalizeHtmlError, postForm } from "@siisel/frontend-shared";
import { OdataMultiSelect } from "./OdataMultiSelect";
import { FORM_FIELD_ID_PREFIX, SAVE_AND_CLOSE_FIELD_NAME } from "../config/constants";
import type { LegacyFormAction, LegacyFormField, LegacyFormFieldValue, LegacyFormSection, LegacyFormViewModel } from "../types";
import {
  appendExternalFormFields,
  appendFieldValue,
  type FieldState,
  getDisplayText,
  getInitialFieldValue,
  normalizeColor,
  shouldDisplayReadOnly,
  stringifyFieldValue
} from "../utils/legacyForm";

function HelpTooltip({ help, title }: { help?: string; title?: string }) {
  if (!help) {
    return null;
  }

  return (
    <span className="forms-app-help-tooltip" tabIndex={0} aria-label={title ? `${title}: ${help}` : help}>
      <span className="material-icons" aria-hidden="true">info_outline</span>
      <span className="forms-app-help-tooltip__popover" role="tooltip">
        {title ? <span className="forms-app-help-tooltip__title">{title}</span> : null}
        <span className="forms-app-help-tooltip__body">{help}</span>
      </span>
    </span>
  );
}

function withHelpTooltip(field: LegacyFormField, control: ReactNode) {
  if (!field.help) {
    return control;
  }

  return (
    <div className="forms-app-input-with-help">
      {control}
      <HelpTooltip help={field.help} title={field.label ?? field.shortLabel} />
    </div>
  );
}

type LegacyFormSectionViewProps = {
  index: number;
  renderAction: (action: LegacyFormAction, index: number) => ReactNode;
  renderField: (field: LegacyFormField) => ReactNode;
  renderFields: (fields: LegacyFormField[]) => ReactNode;
  section: LegacyFormSection;
};

function LegacyFormSectionView({ index, renderAction, renderFields, section }: LegacyFormSectionViewProps) {
  const [isCollapsed, setIsCollapsed] = useState(Boolean(section.collapsible && section.initiallyCollapsed));
  const contentId = `legacy-form-section-${index}`;
  const content = (
    <>
      {renderFields(section.fields)}
      {section.actions?.length ? <div className="forms-app-actions">{section.actions.map(renderAction)}</div> : null}
    </>
  );

  if (!section.collapsible) {
    return (
      <div className="legacy-form-section">
        {section.title ? <h4>{section.title}</h4> : null}
        {content}
      </div>
    );
  }

  return (
    <div className={`legacy-form-section legacy-form-section--collapsible ${isCollapsed ? "is-collapsed" : "is-expanded"}`}>
      <button
        type="button"
        className="legacy-form-section-toggle"
        aria-controls={contentId}
        aria-expanded={!isCollapsed}
        onClick={() => setIsCollapsed((current) => !current)}
      >
        {section.toggleIcon ? <span className="material-icons legacy-form-section-toggle__icon">{section.toggleIcon}</span> : null}
        <span>{section.title}</span>
      </button>
      {isCollapsed ? null : (
        <div id={contentId} className="legacy-form-section-content">
          {content}
        </div>
      )}
    </div>
  );
}

type BooleanSwitchProps = {
  checked: boolean;
  disabled: boolean;
  id: string;
  onChange: (checked: boolean) => void;
};

function BooleanSwitch({ checked, disabled, id, onChange }: BooleanSwitchProps) {
  const className = [
    "forms-app-switch",
    checked ? "forms-app-switch--checked" : "forms-app-switch--unchecked",
    disabled ? "forms-app-switch--disabled" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <label className={className} htmlFor={id}>
      <span className="forms-app-switch__label forms-app-switch__label--no">Ne</span>
      <span className="forms-app-switch__track" aria-hidden="true">
        <span className="forms-app-switch__thumb"></span>
      </span>
      <span className="forms-app-switch__label forms-app-switch__label--yes">Ano</span>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
    </label>
  );
}

export default function LegacyFormApp(props: LegacyFormViewModel) {
  const fields = useMemo(() => props.sections.flatMap((section) => section.fields), [props.sections]);
  const initialState = fields.reduce<FieldState>((result, field) => {
    result[field.name] = getInitialFieldValue(field);
    return result;
  }, {});
  const [values, setValues] = useState<FieldState>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function resolveLookupUrl(field: LegacyFormField, currentValues: FieldState): string | undefined {
    if (!field.lookupUrlField || !field.lookupUrlByFieldValue) {
      return field.lookupUrl;
    }

    const fieldValue = stringifyFieldValue(currentValues[field.lookupUrlField]);
    return field.lookupUrlByFieldValue[fieldValue] ?? field.lookupUrl;
  }

  function setValue(field: LegacyFormField, value: LegacyFormFieldValue) {
    setValues((current) => {
      const next = { ...current, [field.name]: value };
      fields
        .filter((dependentField) => dependentField.lookupUrlField === field.name)
        .forEach((dependentField) => {
          next[dependentField.name] = [];
        });
      return next;
    });
    setErrorMessage(null);
  }

  async function submit(root: HTMLElement | null, action?: LegacyFormAction, saveAndClose = Boolean(props.saveAndCloseValue)) {
    if (isSubmitting || !props.canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const formData = new FormData();
      appendAntiForgeryToken(formData, root);

      fields.filter((field) => field.type !== "link-button").forEach((field) => appendFieldValue(formData, field.name, values[field.name]));
      appendExternalFormFields(formData, props.formId);
      action?.extraFields?.forEach((field) => appendFieldValue(formData, field.name, field.value));
      formData.append(SAVE_AND_CLOSE_FIELD_NAME, String(saveAndClose));

      const response = await postForm(action?.submitUrl ?? props.submitUrl, formData);

      if (response.redirected) {
        window.location.href = response.url;
        return;
      }

      const html = await response.text();
      if (!response.ok) {
        throw new Error(html);
      }

      const validationMessage = extractHtmlError(html);
      if (validationMessage) {
        setErrorMessage(validationMessage);
        return;
      }

      window.location.reload();
    } catch (error) {
      setErrorMessage(normalizeHtmlError(error, "Ulozeni se nepodarilo."));
    } finally {
      setIsSubmitting(false);
    }
  }

  function renderField(field: LegacyFormField) {
    const value = values[field.name];
    const inputId = `${FORM_FIELD_ID_PREFIX}-${field.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
    const type = field.type ?? "text";
    const displayReadOnly = shouldDisplayReadOnly(field, type, props.canSubmit);
    const disabled = isSubmitting || Boolean(field.disabled) || !props.canSubmit || displayReadOnly;

    if (field.hidden || type === "hidden") {
      return null;
    }

    if (type === "link-button") {
      return (
        <div className="form-group forms-app-link-button-row" key={field.name}>
          <a
            id={field.id}
            className={field.className ?? "btn btn-primary btn-sm btn-if"}
            href={field.href ?? "#"}
            data-confirm={field.confirm}
            data-confirm-title={field.confirmTitle}
            onClick={(event) => {
              if (field.disabled) {
                event.preventDefault();
                return;
              }

              if (!field.onClick) {
                return;
              }

              const handler = (window as unknown as Record<string, unknown>)[field.onClick];
              if (typeof handler === "function") {
                (handler as (link: HTMLAnchorElement) => void)(event.currentTarget);
              }
            }}
          >
            {field.iconClassName ? <i className={field.iconClassName}></i> : null}
            {field.iconClassName ? "\u00a0" : null}
            {field.label}
          </a>
        </div>
      );
    }

    const control = withHelpTooltip(field, renderFieldControl(field, inputId, disabled, type, value, displayReadOnly));

    if (type === "checkbox") {
      return (
        <div className="form-group forms-app-toggle-row" key={field.name}>
          <label htmlFor={inputId}>{field.label}</label>
          {control}
        </div>
      );
    }

    if (type === "readonly" || displayReadOnly) {
      return (
        <div className="form-group" key={field.name}>
          <label>{field.label}</label>
          {control}
        </div>
      );
    }

    return (
      <div className="form-group" key={field.name}>
        <label htmlFor={inputId}>{field.label}</label>
        {control}
      </div>
    );
  }

  function renderFieldControl(field: LegacyFormField, inputId: string, disabled: boolean, type: string, value: LegacyFormFieldValue, displayReadOnly = false) {
    const placeholder = field.placeholder ?? field.shortLabel;

    if (type === "checkbox") {
      return <BooleanSwitch id={inputId} checked={Boolean(value)} disabled={disabled} onChange={(nextValue) => setValue(field, nextValue)} />;
    }

    if (type === "readonly" || displayReadOnly) {
      return <span className="text-as-ro-input forms-app-readonly-value">{getDisplayText(field, type, value)}</span>;
    }

    if (type === "textarea") {
      return (
        <textarea
          id={inputId}
          className={field.className ?? "form-control"}
          value={stringifyFieldValue(value)}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => setValue(field, event.currentTarget.value)}
        />
      );
    }

    if (type === "select") {
      return (
        <select
          id={inputId}
          className={field.className ?? "form-control"}
          value={stringifyFieldValue(value)}
          disabled={disabled}
          onChange={(event) => setValue(field, event.currentTarget.value)}
        >
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      );
    }

    if (type === "segmented-radio") {
      const selectedValue = stringifyFieldValue(value);
      return (
        <div className="btn-group btn-group-toggle forms-app-segmented-radio" role="radiogroup" aria-label={field.label}>
          {field.options?.map((option) => {
            const isChecked = option.value === selectedValue;
            return (
              <label
                className={`btn btn-secondary${isChecked ? " active" : ""}${disabled ? " disabled" : ""}`}
                key={option.value}
              >
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  checked={isChecked}
                  disabled={disabled}
                  onChange={() => setValue(field, option.value)}
                />
                {option.text}
              </label>
            );
          })}
        </div>
      );
    }

    if (type === "odata-multiselect") {
      return (
        <OdataMultiSelect
          disabled={disabled}
          field={{ ...field, placeholder }}
          inputId={inputId}
          lookupUrl={resolveLookupUrl(field, values)}
          value={value}
          onChange={(nextValue) => setValue(field, nextValue)}
        />
      );
    }

    if (type === "color") {
      return (
        <input
          id={inputId}
          aria-label={field.label}
          className="forms-app-color-field"
          type="color"
          value={normalizeColor(stringifyFieldValue(value))}
          disabled={disabled}
          onChange={(event) => setValue(field, event.currentTarget.value)}
        />
      );
    }

    return (
      <input
        id={inputId}
        className={field.className ?? "form-control"}
        type={type}
        value={stringifyFieldValue(value)}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => setValue(field, event.currentTarget.value)}
      />
    );
  }

  function renderFieldGroup(groupFields: LegacyFormField[], groupKey: string) {
    const firstField = groupFields[0];
    return (
      <div className="form-group forms-app-field-row" key={groupKey}>
        <label>{firstField.rowLabel ?? firstField.label}</label>
        <div className="forms-app-field-row__inputs">
          {groupFields.map((field) => {
            const value = values[field.name];
            const inputId = `${FORM_FIELD_ID_PREFIX}-${field.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
            const type = field.type ?? "text";
            const displayReadOnly = shouldDisplayReadOnly(field, type, props.canSubmit);
            const disabled = isSubmitting || Boolean(field.disabled) || !props.canSubmit || displayReadOnly;
            return (
              <div className="forms-app-field-row__input" key={field.name}>
                {field.shortLabel ? <label className="sr-only" htmlFor={inputId}>{field.shortLabel}</label> : null}
                {withHelpTooltip(field, renderFieldControl(field, inputId, disabled, type, value, displayReadOnly))}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderFields(sectionFields: LegacyFormField[]) {
    const nodes: ReactNode[] = [];
    let index = 0;

    while (index < sectionFields.length) {
      const field = sectionFields[index];
      if (field.rowGroup && !field.hidden && field.type !== "hidden") {
        const groupFields: LegacyFormField[] = [];
        const rowGroup = field.rowGroup;
        while (index < sectionFields.length && sectionFields[index].rowGroup === rowGroup) {
          if (!sectionFields[index].hidden && sectionFields[index].type !== "hidden") {
            groupFields.push(sectionFields[index]);
          }
          index += 1;
        }
        if (groupFields.length) {
          nodes.push(renderFieldGroup(groupFields, rowGroup));
        }
        continue;
      }

      nodes.push(renderField(field));
      index += 1;
    }

    return nodes;
  }

  function renderAction(action: LegacyFormAction, index: number) {
    return (
      <button
        type="button"
        className={action.className ?? "btn btn-primary btn-sm"}
        disabled={isSubmitting || !props.canSubmit || action.disabled}
        onClick={(event) => void submit(event.currentTarget.closest(".forms-app"), action)}
        key={`${action.label}-${index}`}
      >
        {action.iconClassName ? <em className={action.iconClassName}></em> : null}
        {isSubmitting ? `${action.label}...` : action.label}
      </button>
    );
  }

  const submitFooterControls =
    props.showSubmit === false ? null : (
      <>
        {props.saveAndCloseText ? (
          <div className="btn-group">
            <button
              id={props.submitButtonId}
              type="button"
              className="btn btn-success btn-sm"
              disabled={isSubmitting || !props.canSubmit}
              onClick={(event) => void submit(event.currentTarget.closest(".forms-app"))}
            >
              <em className="fa fa-save"></em>
              {isSubmitting ? `${props.submitText}...` : props.submitText}
            </button>
            <button
              type="button"
              className="btn btn-success btn-sm dropdown-toggle dropdown-toggle-split"
              disabled={isSubmitting || !props.canSubmit}
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
              aria-label="Další možnosti uložení"
            >
              <span className="sr-only">Další možnosti uložení</span>
            </button>
            <span className="dropdown-menu">
              <button
                type="button"
                className="btn btn-success btn-sm"
                disabled={isSubmitting || !props.canSubmit}
                onClick={(event) => void submit(event.currentTarget.closest(".forms-app"), undefined, true)}
              >
                <em className="fa fa-save"></em>
                {isSubmitting ? `${props.saveAndCloseText}...` : props.saveAndCloseText}
              </button>
            </span>
          </div>
        ) : (
          <button
            id={props.submitButtonId}
            type="button"
            className="btn btn-success btn-sm"
            disabled={isSubmitting || !props.canSubmit}
            onClick={(event) => void submit(event.currentTarget.closest(".forms-app"))}
          >
            <em className="fa fa-save"></em>
            {isSubmitting ? `${props.submitText}...` : props.submitText}
          </button>
        )}
        {props.actions?.map(renderAction)}
      </>
    );
  const submitFooter =
    submitFooterControls === null ? null : (
      <div className="panel-footer">
        {submitFooterControls}
      </div>
    );
  const footerPortalTarget = props.footerPortalId ? document.getElementById(props.footerPortalId) : null;
  const portaledFooter =
    footerPortalTarget && submitFooterControls
      ? createPortal(props.footerPortalMode === "contents" ? submitFooterControls : submitFooter, footerPortalTarget)
      : submitFooter;

  return (
    <div className="forms-app legacy-form-app">
      {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> : null}
      <div className="base-form">
        {props.sections.map((section, index) => (
          <LegacyFormSectionView index={index} key={index} renderAction={renderAction} renderField={renderField} renderFields={renderFields} section={section} />
        ))}
      </div>
      {portaledFooter}
    </div>
  );
}
